import { useAuth0 } from '@auth0/auth0-react';
import { accumulateManagedAgentsEvent, type AccumulatedEvent } from '@anthropic-ai/sdk/lib/sessions/accumulate';
import React, { createContext, useContext, useEffect, useCallback, useMemo, useRef, useState } from 'react';
import { streamEvents, useSendUserEvent } from '../api/useAgentChatClient';
import { AgentStreamEvent, SessionStatus } from '../types';
import useRecruitmentId from '@/Hooks/useRecruitmentId';

// --- context ---
type AnthropicManagedAgentSessionContextType = {
    sessionStatus: SessionStatus;
    events: AgentStreamEvent[];
    previewMessage: AccumulatedEvent | undefined;
    lastEventId: string | undefined;
    sendMessage: (text: string) => Promise<void>;
    sendInterruptMessage: () => Promise<void>;
};

const AnthropicManagedAgentSessionContext = createContext<AnthropicManagedAgentSessionContextType | null>(null);

function deriveSessionStatus(events: AgentStreamEvent[]): SessionStatus {
    for (let i = events.length - 1; i >= 0; i--) {
        const { type } = events[i];
        if (type === 'session.status_running') return 'running';
        if (type === 'session.status_idle') return 'idle';
        if (type === 'session.status_rescheduled') return 'rescheduling';
        if (type === 'session.status_terminated') return 'terminated';
        if (type === 'session.error') return 'error';
    }
    // No status event in history — infer from presence of content events
    return events.some(
        (e) => e.type === 'user.message' || e.type === 'agent.message' || e.type === 'agent.mcp_tool_use',
    )
        ? 'idle'
        : 'not_started';
}

export function AnthropicManagedAgentSessionContextProvider({
    sessionId,
    initialEvents = [],
    onMcpToolResult,
    children,
}: {
    sessionId: string;
    initialEvents?: AgentStreamEvent[];
    onMcpToolResult?: (toolName: string) => void;
    children: React.ReactNode;
}) {
    const { getAccessTokenSilently } = useAuth0();
    const recruitmentId = useRecruitmentId();

    const [events, setEvents] = useState<AgentStreamEvent[]>(initialEvents);
    const [sessionStatus, setSessionStatus] = useState<SessionStatus>(() => deriveSessionStatus(initialEvents));
    const [previewMessage, setPreviewMessage] = useState<AccumulatedEvent | undefined>(undefined);
    const [lastEventId, setLastEventId] = useState<string | undefined>(() => {
        for (let i = initialEvents.length - 1; i >= 0; i--) {
            const e = initialEvents[i] as any;
            if (e.id) return e.id as string;
        }
        return undefined;
    });
    // Track seen event IDs to deduplicate SSE replays against initialEvents
    const seenEventIds = useRef(new Set(initialEvents.map((e: any) => e.id).filter(Boolean)));

    const { trigger: postEventTrigger } = useSendUserEvent(recruitmentId, sessionId);

    useEffect(() => {
        let abortController: AbortController | null = null;
        let reconnectTimeout: ReturnType<typeof setTimeout> | null = null;

        async function connect() {
            abortController?.abort();
            abortController = new AbortController();
            const { signal } = abortController;
            const toolUseNames = new Map<string, string>();

            try {
                const token = await getAccessTokenSilently();

                if (signal.aborted) {
                    return;
                }

                for await (const event of streamEvents(sessionId, token, signal)) {
                    setPreviewMessage((prev) => {
                        try {
                            return accumulateManagedAgentsEvent(prev, event);
                        } catch {
                            // A dropped/reconnected SSE connection can deliver an event_delta
                            // without its event_start if the gap straddles a message boundary.
                            // The preview is always provisional — drop it rather than crash;
                            // the buffered final event (handled below) remains authoritative.
                            return undefined;
                        }
                    });

                    if (event.type === 'agent.mcp_tool_use') {
                        const e = event as any;
                        toolUseNames.set(e.id, e.name);
                    } else if (event.type === 'agent.mcp_tool_result') {
                        // the logic updates side bar counters
                        const toolName = toolUseNames.get((event as any).mcp_tool_use_id);
                        if (toolName) {
                            onMcpToolResult?.(toolName);
                        }
                    }
                    if (event.type === 'event_start' || event.type === 'event_delta') {
                        // Live-preview frames — handled above via setPreviewMessage.
                        // They carry no top-level id and are never persisted, so they
                        // must not be pushed into the events[] history.
                    } else if (event.type === 'session.status_idle') {
                        setSessionStatus('idle');
                    } else if (event.type === 'session.status_running') {
                        setSessionStatus('running');
                    } else if (event.type === 'session.status_rescheduled') {
                        setSessionStatus('rescheduling');
                    } else if (event.type === 'session.status_terminated') {
                        setSessionStatus('terminated');
                    } else if (event.type === 'session.error') {
                        setSessionStatus('error');
                    } else {
                        const eventId = 'id' in event ? (event.id as string | undefined) : undefined;
                        if (eventId && seenEventIds.current.has(eventId)) {
                            // SSE is replaying an event already loaded from history — skip
                        } else {
                            if (eventId) seenEventIds.current.add(eventId);
                            setEvents((prev) => [...prev, event]);
                            if (eventId) setLastEventId(eventId);
                        }
                    }
                }
            } catch (err) {
                // TODO: fix the error name or check if anthropic return it
                if (!signal.aborted && !(err instanceof Error && err.name === 'AbortError')) {
                    // try to reconnect only if not deliberately aborted
                    reconnectTimeout = setTimeout(connect, 2000);
                }
            }
        }
        connect();
        return () => {
            abortController?.abort();
            if (reconnectTimeout !== null) clearTimeout(reconnectTimeout);
        };
    }, [sessionId, recruitmentId, getAccessTokenSilently]);

    const sendMessage = useCallback(
        async (text: string) => {
            await postEventTrigger({ type: 'user.message', text });
        },
        [postEventTrigger],
    );

    const sendInterruptMessage = useCallback(async () => {
        await postEventTrigger({ type: 'user.interrupt' });
    }, [postEventTrigger]);

    const value = useMemo(
        () => ({
            sessionStatus,
            events,
            previewMessage,
            lastEventId,
            sendMessage,
            sendInterruptMessage,
        }),
        [sessionStatus, events, previewMessage, lastEventId, sendMessage],
    );

    return (
        <AnthropicManagedAgentSessionContext.Provider value={value}>
            {children}
        </AnthropicManagedAgentSessionContext.Provider>
    );
}

export function useAnthropicManagedAgentSession() {
    const ctx = useContext(AnthropicManagedAgentSessionContext);
    if (!ctx) {
        throw new Error('useAnthropicManagedAgentSession must be inside <AnthropicManagedAgentSessionContextProvider>');
    }
    return ctx;
}
